import jwtDecode from "jwt-decode";
import { takeLatest, takeLeading, call, put, select } from "redux-saga/effects";
import { verify, Type, optional } from "specified";
import { DataLayer } from "@grandvisionhq/analytics";
import { QueryString, StringGuard, getSessionStorageItem } from "@grandvisionhq/utils";
import { goto } from "../../actions.js";
import { crossOriginAuthentication, parseHash as parseHash$1, userInfo, checkSession as checkSession$1 } from "../../auth.js";
import { getAuth } from "../../selectors.js";
import { isLoggedIn, isUninitialized } from "../../state.js";
import { clearTokenCache, cacheToken } from "../../token-service.js";
import { LOGIN, PARSE_HASH, PARSE_HASH_SUCCEEDED, PARSE_HASH_FAILED, RECOVER_SESSION, ASSERT_SESSION, loginFailed, parseHashFailed, recoverSessionFailed, assertSessionFailed, parseHashSucceeded } from "./actions.js";
import { isExpiredLinkError } from "../utils/Login/utils/is-expired-link-error.js";
import { removeUserDataFromLS } from "../../Util/Util/customer-name-persistor.js";
import { logOut } from "../../Logout/Logout/actions.js";
const RETURN_PATH_STORAGE_KEY = "login_returnPath";
function* login(action) {
  const { returnPath } = action.payload;
  try {
    if (window && returnPath) {
      try {
        yield call([sessionStorage, "setItem"], RETURN_PATH_STORAGE_KEY, returnPath);
        try {
          yield call([localStorage, "setItem"], RETURN_PATH_STORAGE_KEY, returnPath);
        } catch (localStorageErr) {
          console.warn("[Auth] Could not store returnPath in localStorage:", localStorageErr);
        }
      } catch (e) {
        console.error("[Auth] Failed to store returnPath in storage:", e);
      }
    }
    yield call(crossOriginAuthentication, action.payload.email, action.payload.password, {
      scope: "openid profile email"
    });
  } catch (err) {
    yield put(loginFailed(err));
  }
}
const stateSpec = Type.interface({
  accountVerificationMethod: optional(Type.string),
  email: optional(Type.string),
  targetAction: optional(
    Type.literal({
      forgotPassword: 1,
      signUp: 1
    })
  )
});
function parseState(state) {
  try {
    const result = JSON.parse(state);
    return verify(stateSpec, result).value();
  } catch (err) {
    return void 0;
  }
}
function* parseHash() {
  let state = "TODO:csrf";
  let loginMethod = "";
  try {
    try {
      const hash = QueryString.parse(window.location.hash.substring(1));
      if (hash.state) {
        state = StringGuard.guard("hash.state", hash.state);
      }
      const search = QueryString.parse(window.location.search);
      if (search.loginMethod) {
        loginMethod = StringGuard.guard("search.loginMethod", search.loginMethod);
      }
    } catch (e) {
      console.error("Error while parsing URL hash:", e);
    }
    const result = yield call(parseHash$1, {
      state
    });
    if (!result.accessToken) {
      yield put(
        parseHashFailed({
          error: new Error("No accessToken received after login"),
          ...parseState(state)
        })
      );
      return;
    }
    const accountVerificationMethod = parseState(state)?.accountVerificationMethod;
    if (loginMethod === "social") {
      yield call(clearTokenCache);
      return;
    }
    yield call(onAccessTokenReceived, result.accessToken, true, accountVerificationMethod);
  } catch (error) {
    yield put(parseHashFailed({ error, ...parseState(state) }));
  }
}
function* redirectAfterParseHash(action) {
  if (!action.payload.redirect) {
    return;
  }
  let route = "me.profile";
  if (action.payload.type === "passwordless") {
    if (action.payload.hasActivatedAccount) {
      route = "auth.set-password";
    } else if (action.payload.accountVerificationMethod === "dateOfBirth") {
      route = "auth.verify";
    } else {
      route = "auth.create";
    }
    yield put(goto({ route }));
    return;
  }
  let sessionReturnPath = null;
  let localReturnPath = null;
  let storageChecked = false;
  sessionReturnPath = yield call(getSessionStorageItem, RETURN_PATH_STORAGE_KEY);
  storageChecked = true;
  if (!sessionReturnPath) {
    try {
      localReturnPath = yield call([localStorage, "getItem"], RETURN_PATH_STORAGE_KEY);
    } catch (e) {
      console.warn("[Auth] Could not retrieve returnPath from localStorage:", e);
    }
  }
  const finalReturnPath = sessionReturnPath || localReturnPath;
  if (finalReturnPath) {
    yield put(goto({ path: decodeURIComponent(finalReturnPath) }));
    yield call(() => {
      setTimeout(() => {
        try {
          sessionStorage.removeItem(RETURN_PATH_STORAGE_KEY);
          localStorage.removeItem(RETURN_PATH_STORAGE_KEY);
        } catch (e) {
          console.warn("[Auth] Could not remove returnPath from storage:", e);
        }
      }, 100);
    });
    return;
  }
  if (storageChecked) {
    console.warn(
      "[Auth] No returnPath found in either sessionStorage or localStorage; falling back to default route:",
      route
    );
  }
  yield put(goto({ route }));
}
function* redirectAfterFailedParseHash(action) {
  const { error, targetAction } = action.payload;
  const query = {};
  if ("error" in error) {
    const description = error.error_description || error.errorDescription;
    if (description && description.startsWith("gv__")) {
      query.error = description.substring("gv__".length);
    }
  }
  let route = "auth.login";
  if (targetAction === "forgotPassword" || targetAction === "signUp") {
    route = "auth.link-expired";
    query.targetAction = targetAction;
  } else if (isExpiredLinkError(error)) {
    route = "auth.link-expired";
  }
  yield put(
    goto({
      route,
      query: Object.keys(query).length > 0 ? QueryString.stringify(query, true) : void 0
    })
  );
}
function* checkSession() {
  const authState = yield select(getAuth);
  if (isUninitialized(authState)) {
    return;
  }
  const result = yield call(checkSession$1, {
    scope: "openid profile",
    responseType: "id_token token"
  });
  if (!result.accessToken) {
    throw new Error("No accessToken received during session recovery");
  }
  return result;
}
function* redirectOnFailedSession() {
  yield call(removeUserDataFromLS);
  yield call(clearTokenCache);
  yield put(logOut());
}
function* assertSession() {
  let error;
  try {
    const result = yield call(checkSession);
    if (!result?.accessToken) {
      yield call(redirectOnFailedSession);
      error = new Error("No access token received during session assertion.");
    }
  } catch (err) {
    if (err.error === "login_required") {
      yield call(redirectOnFailedSession);
      error = new Error("No access token received during session assertion.");
    } else {
      error = err;
    }
  }
  if (error) {
    yield put(assertSessionFailed(error));
  }
}
function* recoverSession() {
  try {
    const result = yield call(checkSession);
    if (!result?.accessToken) {
      yield put(
        recoverSessionFailed(new Error("No accessToken received during session recovery"))
      );
      return;
    }
    yield call(onAccessTokenReceived, result.accessToken, false);
    const authState = yield select(getAuth);
    if (isLoggedIn(authState)) {
      const userId = authState.userId.split("|")[1] ?? "";
      const userEmail = authState.email;
      yield call(DataLayer.setUserObjectDataLayer, userId, userEmail);
    }
  } catch (err) {
    yield put(recoverSessionFailed(err));
  }
}
function* onAccessTokenReceived(accessToken, redirect, accountVerificationMethod) {
  const jwtContent = yield call(jwtDecode, accessToken);
  let email = jwtContent["https://grandvision/claims/email"];
  if (!email) {
    const userInfo$1 = yield call(userInfo, accessToken);
    if (!userInfo$1.email) {
      throw new Error(
        `Email claim should be in accessToken or accessToken should have at least 'openid email' scope`
      );
    }
    email = userInfo$1.email;
  }
  yield call(cacheToken, accessToken);
  const opts = {
    id: jwtContent.sub,
    email,
    redirect
  };
  if (jwtContent["https://grandvision/claims/connection"] === "email") {
    yield put(
      parseHashSucceeded({
        ...opts,
        type: "passwordless",
        hasActivatedAccount: jwtContent["https://grandvision/claims/activeAccount"] || false,
        accountVerificationMethod
      })
    );
  } else {
    yield put(
      parseHashSucceeded({
        ...opts,
        type: "regular"
      })
    );
  }
}
const loginSagas = [
  takeLatest(LOGIN, login),
  takeLatest(PARSE_HASH, parseHash),
  takeLatest(PARSE_HASH_SUCCEEDED, redirectAfterParseHash),
  takeLatest(PARSE_HASH_FAILED, redirectAfterFailedParseHash),
  takeLeading(RECOVER_SESSION, recoverSession),
  takeLeading(ASSERT_SESSION, assertSession)
];
export {
  assertSession,
  checkSession,
  loginSagas as default,
  login,
  onAccessTokenReceived,
  parseHash,
  recoverSession,
  redirectAfterFailedParseHash,
  redirectAfterParseHash,
  redirectOnFailedSession
};
//# sourceMappingURL=sagas.js.map
